import type { SourceRef, SourceSnippet, TenderDocument } from "../../types"
import type { TenderExtractionApi } from "@/api/tender-api"

// ── Types ────────────────────────────────────────────────────────────────────

export const EXTRACTION_CITATION_REGEX = /\[\[req:([A-Z]+-\d+)\]\]/g

type ExtractionItemType = "requirement" | "term" | "role" | "deliverable" | "date" | "criterion"

export type ExtractionItem = {
  id: string
  type: ExtractionItemType
  label: string
  category?: string
  source?: SourceRef
  snippet?: SourceSnippet
  isMandatory?: boolean
  riskLevel?: string
  urgency?: string
  extra?: Record<string, unknown>
}

export type ExtractionItemLookup = Map<string, ExtractionItem>

// ── Lookup builder ───────────────────────────────────────────────────────────

export function buildExtractionLookup(
  data: TenderExtractionApi | null | undefined
): ExtractionItemLookup {
  const map: ExtractionItemLookup = new Map()
  if (!data) return map

  for (const r of data.requirements) {
    map.set(r.id, {
      id: r.id,
      type: "requirement",
      label: r.description,
      category: r.category,
      source: r.source as SourceRef | undefined,
      snippet: r.snippet as SourceSnippet | undefined,
      isMandatory: r.isMandatory,
    })
  }
  for (const t of data.contractTerms) {
    map.set(t.id, {
      id: t.id,
      type: "term",
      label: `${t.clause}: ${t.description}`,
      source: t.source as SourceRef | undefined,
      snippet: t.snippet as SourceSnippet | undefined,
      riskLevel: t.riskLevel,
    })
  }
  for (const r of data.roleRequirements) {
    map.set(r.id, {
      id: r.id,
      type: "role",
      label: r.roleName,
      source: r.source as SourceRef | undefined,
      snippet: r.snippet as SourceSnippet | undefined,
      extra: { quantity: r.quantity, educationLevel: r.educationLevel ?? undefined },
    })
  }
  for (const d of data.bidDeliverables) {
    map.set(d.id, {
      id: d.id,
      type: "deliverable",
      label: `${d.name}: ${d.description}`,
      source: d.source as SourceRef | undefined,
      snippet: d.snippet as SourceSnippet | undefined,
      isMandatory: d.isRequired,
    })
  }
  for (const d of data.criticalDates) {
    map.set(d.id, {
      id: d.id,
      type: "date",
      label: d.event,
      snippet: d.snippet as SourceSnippet | undefined,
      urgency: d.urgency,
      extra: { date: d.date, daysRemaining: d.daysRemaining },
    })
  }
  for (const c of data.evaluationCriteria) {
    map.set(c.id, {
      id: c.id,
      type: "criterion",
      label: `${c.name}: ${c.description}`,
      source: c.source as SourceRef | undefined,
      snippet: c.snippet as SourceSnippet | undefined,
      extra: { weightPercent: c.weightPercent },
    })
  }

  return map
}

// ── Citation parser ──────────────────────────────────────────────────────────

export type CitationPart = { type: "text"; value: string } | { type: "citation"; id: string }

export type DocumentNameLookup = Map<string, string>

export function normalizeDocumentNameLookupKey(value: string): string {
  return value.trim().normalize("NFC").toLowerCase()
}

export function buildDocumentNameLookup(
  documents: TenderDocument[],
  extractionData?: TenderExtractionApi | null
): DocumentNameLookup {
  const map: DocumentNameLookup = new Map()
  for (const doc of documents) {
    map.set(normalizeDocumentNameLookupKey(doc.filename), doc.id)
    const nameWithoutExt = doc.filename.replace(/\.[^/.]+$/, "")
    if (nameWithoutExt !== doc.filename) {
      map.set(normalizeDocumentNameLookupKey(nameWithoutExt), doc.id)
    }
  }
  if (extractionData) {
    const allItems = [
      ...extractionData.requirements,
      ...extractionData.contractTerms,
      ...extractionData.roleRequirements,
      ...extractionData.bidDeliverables,
      ...extractionData.evaluationCriteria,
      ...extractionData.criticalDates,
    ]
    for (const item of allItems) {
      for (const key of ["source", "snippet"] as const) {
        const ref = (item as Record<string, unknown>)[key] as
          | { documentName?: string; documentId?: string }
          | undefined
        if (ref?.documentName && ref.documentId) {
          map.set(normalizeDocumentNameLookupKey(ref.documentName), ref.documentId)
        }
      }
    }
  }
  return map
}

export function normalizeCitationId(id: string): string {
  return id.trim().toUpperCase()
}

export function parseExtractionCitations(text: string): CitationPart[] {
  const parts: CitationPart[] = []
  let lastIndex = 0
  const regex = new RegExp(EXTRACTION_CITATION_REGEX.source, "g")

  let match = regex.exec(text)
  while (match !== null) {
    if (match.index > lastIndex) {
      parts.push({ type: "text", value: text.slice(lastIndex, match.index) })
    }
    parts.push({ type: "citation", id: normalizeCitationId(match[1]) })
    lastIndex = regex.lastIndex
    match = regex.exec(text)
  }

  if (lastIndex < text.length) {
    parts.push({ type: "text", value: text.slice(lastIndex) })
  }

  return parts
}
